import { useCreateIndividualForm } from 'features/create-individual/hooks/use-create-individual-form/useCreateIndividualForm';
import { isEmpty } from 'lodash';
import { createContext, FC, useCallback, useContext, useState } from 'react';
import { useBoolean, useList } from 'react-use';
import { ListActions } from 'react-use/lib/useList';
import {
  Snackbar,
  SnackbarType,
  useCreateIndividualSnackbars
} from './helpers/useCreateIndividualSnackbars';
import { usePreviewPerson } from './helpers/usePreviewPerson';

type Person = {
  firstName: string;
  lastName: string;
  active: boolean;
  href: string;
  id: string;
  mailMatched: boolean;
  phoneMatched: boolean;
  username?: string;
};

type CreateIndividualValues = {
  phoneSnackbar: Snackbar;
  emailSnackbar: Snackbar;
  apiSnackbar: Snackbar;
  searchErrorSnackbar: Snackbar;
  peopleHaveSameContacts: Person[];
  peopleInPingHaveSameContacts: Person[];
  usedPhone: string;
  usedMail: string;
  isPreviewingPerson: boolean;
  formMethods: typeof useCreateIndividualForm;

  previewPerson: (values: { href?: string }) => void;
  toggleIsPreviewingPerson: (nextValue?: boolean) => void;
  showContactWarningSnackbar: (type: SnackbarType, contactFields: any) => void;
  setUsedPhone: (usedPhone: string) => void;
  setUsedMail: (usedMail: string) => void;
  setPeopleHaveSomeContacts: ListActions<Person>;
  setPeopleInPingHaveSameContacts: ListActions<Person>;
  resetVerifyInfoData: () => void;
};
export const defaultFormValues = {
  firstName: '',
  lastName: '',
  manheimMail: '',
  manheimMobileTelephone: '',
  roleName: '',
};

export const CreateIndividualCtx = createContext<CreateIndividualValues>(
  {} as CreateIndividualValues
);

export const CreateIndividualProvider: FC = ({ children }) => {
  const [usedPhone, setUsedPhone] = useState<string>('');
  const [usedMail, setUsedMail] = useState<string>('');
  const [isPreviewingPerson, toggleIsPreviewingPerson] = useBoolean(false);
  const formMethods = useCreateIndividualForm({ defaultFormValues });
  const { previewPerson } = usePreviewPerson();
  const snackbarsState = useCreateIndividualSnackbars({
    previewPerson,
    clearErrors: formMethods.clearErrors,
  });

  const [peopleHaveSameContacts, setPeopleHaveSomeContacts] = useList<Person>(
    []
  );
  const [
    peopleInPingHaveSameContacts,
    setPeopleInPingHaveSameContacts,
  ] = useList<Person>([]);

  const resetVerifyInfoData = useCallback(() => {
    setUsedMail('');
    setUsedPhone('');
    setPeopleHaveSomeContacts.set([]);
  }, [setPeopleHaveSomeContacts]);

  const value: CreateIndividualValues = {
    ...snackbarsState,
    formMethods: formMethods as any,
    peopleHaveSameContacts,
    peopleInPingHaveSameContacts,
    usedPhone,
    usedMail,
    isPreviewingPerson,

    toggleIsPreviewingPerson,
    previewPerson,
    setUsedPhone,
    setUsedMail,
    setPeopleHaveSomeContacts,
    setPeopleInPingHaveSameContacts,
    resetVerifyInfoData,
  };

  return (
    <CreateIndividualCtx.Provider value={value}>
      {children}
    </CreateIndividualCtx.Provider>
  );
};

export const useIndividualProvider = () => {
  const values = useContext(CreateIndividualCtx);
  if (isEmpty(values))
    throw new Error('CreateIndividualCtx must be use in provider');
  return values;
};
